import axios from "axios";
import React, {
  Suspense,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import GallerySlider from "../../Components/GallerySlider";
import ListingCalendar from "../../Components/ListingCalendar";
import toast from "react-hot-toast";
import { differenceInDays } from "date-fns";
import PricingTable from "../../Components/PricingTable";
import { DataContext } from "../../Context/Context";
import MapView from "../../Components/MapView";
import AmenitiesList from "../../Components/AmenitiesList";
import CustomDateRangePicker from "../../Components/CustomDateRangePicker";
import { fetchListingFeeSettings } from "../../Components/fetchListingFeeSettings";
import { fetchListingTaxSettings } from "../../Components/fetchListingTaxSettings";
import { calculateTaxes } from "../../Components/calculateTaxes";
import { handleDateAvailability } from "../../utilities/checkDateAvailability";
import { formatTime } from "../../utilities/formatTime";
import { formatDate } from "../../utilities/formatDate";
import { calculateWeeklyDiscount } from "../../utilities/discountCalculate";
import { splitDescription } from "../../Components/splitDescription";
import { ArrowLeft } from "lucide-react";
import TabbyPromoSnippet from "../../Components/TabbyPromoSnippet";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { generateVacationRentalSchema } from "../../utilities/generateVacationRentalSchema";

const SingleListing = () => {
  const [listing, setListing] = useState({});
  const { slug } = useParams();

  useEffect(() => {
    const fetchListing = async () => {
      try {
        const response = await axios.get(
          `https://365luxuryhomes.com/api/listings/${slug}`
        );
        setListing(response.data);
      } catch (error) {
        console.error("Error fetching listing:", error);
      }
    };

    fetchListing();
  }, [slug]);

  const { user, endDate, startDate, totalPrice } = useContext(DataContext);

  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [selectedServices, setSelectedServices] = useState([]);
  const [listingSetting, setListingSetting] = useState([]);
  const [updatedPrice, setUpdatedPrice] = useState(null);
  const [priceLocked, setPriceLocked] = useState(false);
  const [nights, setNights] = useState(null);
  const [nightsLocked, setNightsLocked] = useState(false);
  const [tax, setTax] = useState([]);
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState([]);
  const [weeklyDiscount, setWeeklyDiscount] = useState(0);
  const [subtotalData, setSubtotalData] = useState(null);
  let [countGuest, setCountGuest] = useState(0);
  let [countChild, setCountChild] = useState(0);
  const [visible, setVisible] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarData, setCalendarData] = useState({});
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [availabilityError, setAvailabilityError] = useState(null);
  const [tabbyTotal, setTabbyTotal] = useState(null);
  const [checkAvailabilityLoading, setCheckAvailabilityLoading] =
    useState(false);

  const guestRef = useRef(null);
  const guestRefTwo = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickOutsideFirst =
        guestRef.current && !guestRef.current.contains(event.target);
      const isClickOutsideSecond =
        guestRefTwo.current && !guestRefTwo.current.contains(event.target);

      // Only hide if clicked outside both
      if (isClickOutsideFirst && isClickOutsideSecond) {
        setVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch listing fees
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await fetchListingFeeSettings(listing?.id);
        setListingSetting(result);
      } catch (err) {
        setListingSetting(null);
      } finally {
        setLoading(false);
      }
    };

    if (listing?.id) {
      fetchData();
    }
  }, [listing?.id]);

  let bedLineFee = listingSetting?.find(
    (item) => item.feeType === "bedLinenFee"
  );

  // If not found, create a default one
  if (!bedLineFee) {
    bedLineFee = {
      feeType: "bedLinenFee",
      amount: 300,
      amountType: "flat",
    };
  } else if (bedLineFee.amount == null || bedLineFee.amount === undefined) {
    bedLineFee.amount = 300;
  }

  // Fetch tax settings
  useEffect(() => {
    const fetchTaxSettings = async () => {
      try {
        const settings = await fetchListingTaxSettings(listing?.id);
        setTax(settings || []);
      } catch (error) {
        console.error("Failed to fetch listing tax settings:", error.message);
      }
    };

    if (listing?.id) {
      fetchTaxSettings();
    }
  }, [listing?.id]);

  let lat = listing?.lat || "";
  let long = listing?.lng || "";

  useEffect(() => {
    setServicesLoading(true);
    const fetchServices = async () => {
      try {
        const response = await axios.get(
          "https://365luxuryhomes.com/api/services"
        );
        setServices(response.data);
      } catch (error) {
        // console.error("Error fetching calendar for the listing:", error);
      } finally {
        setServicesLoading(false);
      }
    };
    fetchServices();
  }, []);

  const handleServiceChange = (e, service) => {
    const { checked } = e.target;
    setSelectedServices((prev) => {
      if (checked) {
        return [...prev, service];
      } else {
        return prev.filter((item) => item._id !== service._id);
      }
    });
  };

  // handle counting Guest, Childs
  const handleCounter = (value, type) => {
    const updateCount = (count, setCount, limit) => {
      setCount(
        value === "minus" ? Math.max(0, count - 1) : Math.min(limit, count + 1)
      );
    };
    if (type === "guest") {
      updateCount(
        countGuest,
        setCountGuest,
        listing?.personCapacity - 1 - countChild
      );
    } else if (type === "child") {
      updateCount(
        countChild,
        setCountChild,
        listing?.personCapacity - 1 - countGuest
      );
    }
  };

  let dateObject = new Date(endDate);
  dateObject.setDate(dateObject.getDate() - 1);
  let newFormattedDate = formatDate(dateObject);

  useEffect(() => {
    const fetchCalendarForListing = async () => {
      setCalendarLoading(true);
      try {
        // Fetch calendar data for the single listing
        const response = await axios.get(
          `https://365luxuryhomes.com/api/listing/calendar`,
          {
            params: {
              listingId: listing?.id,
              startDate: formatDate(startDate),
              endDate: formatDate(endDate),
            },
          }
        );
        const filteredData = response?.data?.filter(
          (item) => item.date !== formatDate(endDate)
        );
        setCalendarData(filteredData);
      } catch (error) {
        // console.error("Error fetching calendar for the listing:", error);
      } finally {
        setCalendarLoading(false);
      }
    };
    if (listing?.id && formatDate(startDate) && newFormattedDate) {
      fetchCalendarForListing();
    }
  }, [listing?.id, formatDate(startDate), newFormattedDate, listing?.id]);

  useEffect(() => {
    if (priceLocked) {
      const basePrice = calendarData?.reduce(
        (sum, item) => sum + (item.price || 0),
        0
      );

      setUpdatedPrice(basePrice);
      setSubtotalData((prevData) => ({
        ...prevData,
        updatedPrice: basePrice,
      }));
    }
  }, [calendarData, priceLocked]);

  useEffect(() => {
    if (nightsLocked) {
      const totalNights = differenceInDays(endDate, startDate);
      setNights(totalNights);
      setSubtotalData((prevData) => ({
        ...prevData,
        nights: totalNights,
      }));
      setNightsLocked(true);
    }
  }, [nights, nightsLocked, startDate, endDate]);

  useEffect(() => {
    if (nightsLocked && updatedPrice) {
      const discount = calculateWeeklyDiscount(nights, updatedPrice);
      setWeeklyDiscount(discount);
    }
  }, [nights, updatedPrice, nightsLocked]);

  const handleBookNow = () => {
    if (!priceLocked && calendarData) {
      const handleTotalPrice = calendarData?.reduce(
        (sum, item) => sum + (item.price || 0),
        0
      );

      setUpdatedPrice(handleTotalPrice);
      setSubtotalData((prevData) => ({
        ...prevData,
        updatedPrice: handleTotalPrice,
      }));
      setPriceLocked(true);

      // Calculate and set weekly discount
      const discount = calculateWeeklyDiscount(nights, totalPrice);
      setWeeklyDiscount(discount);
    }
  };

  const calculateNights = () => {
    if (!nightsLocked) {
      const totalNights = differenceInDays(endDate, startDate);
      setNights(totalNights);
      setSubtotalData((prevData) => ({
        ...prevData,
        nights: totalNights,
      }));
      setNightsLocked(true);
    }
  };

  const checkAvailability = async (startDate, endDate) => {
    setIsCheckingAvailability(true);
    setAvailabilityError(null);

    try {
      // Check if dates are already booked
      const availabilityResponse = await axios.get(
        `https://365luxuryhomes.com/api/listing/calendar`,
        {
          params: {
            listingId: listing?.id,
            startDate: formatDate(startDate),
            endDate: formatDate(endDate),
          },
        }
      );

      const dates = availabilityResponse?.data;
      const isBooked = dates.some((date) => date.status === "available");

      if (!isBooked) {
        throw new Error("Selected dates are no longer available");
      }

      return true;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      setAvailabilityError(errorMessage);
      toast.error(errorMessage);
      return false;
    } finally {
      setIsCheckingAvailability(false);
    }
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    setCheckAvailabilityLoading(true);

    if (!startDate || !endDate) {
      toast.error("Please provide start & end date");
      setCheckAvailabilityLoading(false);
      return;
    }

    const guestData = e.target[1].value;
    if (!guestData) {
      toast.error("Please select number of guests");
      setCheckAvailabilityLoading(false);
      return;
    }

    // Check real-time availability before proceeding
    const isAvailable = await checkAvailability(startDate, endDate);
    if (!isAvailable) {
      setCheckAvailabilityLoading(false);
      return;
    }

    const [guestCount, childCount = null, infantCount = null] = guestData
      .match(/\d+/g)
      .map(Number);
    const cleaningFee = listing?.cleaningFee;
    const additionalValue = selectedServices.reduce(
      (total, service) => total + service.value,
      0
    );

    const formattedStartDate = formatDate(startDate);
    const formattedEndDate = formatDate(endDate);

    // Calculate final price with weekly discount
    const finalPrice = updatedPrice;

    setSubtotalData({
      formattedStartDate,
      formattedEndDate,
      countGuest,
      childCount,
      infantCount,
      nights,
      updatedPrice: finalPrice, // Send price after weekly discount
      cleaningFee,
      bedLineFee,
      startDate,
      endDate,
      selectedServices,
      additionalValue,
      user,
      listing,
    });

    handleBookNow();
    calculateNights();
    setCheckAvailabilityLoading(false);
  };

  // Final step navigate to checkout page with booking information
  const handleCheckout = () => {
    const start = formatDate(startDate);
    const end = formatDate(endDate);
    const isAvailable = handleDateAvailability(calendarData, start, end);

    if (!isAvailable) {
      toast.error("Selected dates are not available");
      return;
    }
    navigate(`/checkout?ref=${listing.id}`, {
      state: {
        ...subtotalData,
        countGuest,
        formattedStartDate: formatDate(startDate),
        formattedEndDate: formatDate(endDate),
      },
    });
  };

  const handlePricingError = (error) => {
    toast.error(error);
  };

  const { cityTourismTax, occupancyTax, vatGstTax } = calculateTaxes(
    tax,
    subtotalData?.nights,
    subtotalData?.updatedPrice
  );

  let sections;
  if (listing) {
    sections = splitDescription(listing?.description);
  }

  const [searchParams] = useSearchParams();
  const passedStartDate = searchParams.get("startDate");
  const passedEndDate = searchParams.get("endDate");
  const passedGuest = searchParams.get("guest");

  useEffect(() => {
    setCountGuest(passedGuest || 0);
  }, []);

  const location = useLocation();

  const canonicalUrl = `https://365luxuryhomes.com${location.pathname}`;
  const schemaData = generateVacationRentalSchema(listing);

  return (
    <>
      {listing && (
        <Helmet key={listing?.id}>
          <title>{`${listing?.bookingcomPropertyRoomName || "Apartment"} in ${
            listing?.city || "Dubai"
          } | ${listing?.listingAmenities
            ?.filter((a) => a?.amenityName.toLowerCase()?.includes("pool"))
            ?.map((a, i) => a?.amenityName)} | 365 Luxury Homes`}</title>
          <meta
            name="description"
            content={`${listing?.description?.slice(0, 120)}...`}
          />
          <link rel="canonical" href={canonicalUrl} />
          <script type="application/ld+json">
            {JSON.stringify(schemaData)}
          </script>
        </Helmet>
      )}
      <section className="lg:pt-28 md:pt-24 pt-24 pb-10">
        <div className="container">
          {loading ? (
            <div className="flex items-center mb-4 gap-4">
              <div className="skeleton h-10 w-10 rounded-full"></div>
              <div className="skeleton h-8 w-96"></div>
            </div>
          ) : (
            <div className="flex items-center mb-4 gap-4">
              <span
                onClick={() => navigate(-1)}
                className="p-2 bg-[#e6e6e6] rounded-full block w-10 h-10 text-[#c3a963] hover:bg-[#c3a963] hover:text-white cursor-pointer"
              >
                <ArrowLeft size={24} className="" />
              </span>
              <h1 className="leading-normal font-medium text-[#74716d] lg:text-[1.6rem] md:text-[1.4rem] text-[1rem] ">
                {listing?.name}
              </h1>
            </div>
          )}

          <div className="flex lg:gap-8 md:gap-5 relative">
            {loading ? (
              <div className="lg:w-[70%] md:w-[70%] w-full">
                <div className="flex w-full flex-col gap-4">
                  <div className="skeleton lg:h-[450px] md:h-[350px] h-40 w-full"></div>
                  <div className="flex items-center gap-4">
                    <div className="skeleton lg:h-24 w-44"></div>
                    <div className="skeleton lg:h-24 w-44"></div>
                    <div className="skeleton lg:h-24 w-44"></div>
                    <div className="skeleton lg:h-24 w-44"></div>
                  </div>
                  <div className="skeleton h-4 w-28"></div>
                  <div className="skeleton h-4 w-full"></div>
                  <div className="skeleton h-4 w-full"></div>
                  <div className="skeleton h-4 w-full"></div>
                  <div className="skeleton h-4 w-full"></div>
                  <div className="skeleton h-4 w-full"></div>
                </div>
              </div>
            ) : (
              <div className="lg:w-[70%] sm-lg:w-[65%] w-full">
                <div className="relative z-0">
                  <Suspense
                    fallback={<div className="skeleton h-72 w-full"></div>}
                  >
                    <GallerySlider images={listing?.listingImages} />
                  </Suspense>
                </div>
                <div className="flex w-full lg:flex-row md:flex-row flex-col items-center justify-between mt-4">
                  <div className="lg:w-auto md:w-auto w-full lg:flex md:flex grid grid-cols-2 gap-4 bg-[#f9f9f9] p-3 text-[#74716d] text-[16px] rounded-lg">
                    <div className="flex items-center gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width={20}
                        height={20}
                        className="text-inherit text-[#c3a963]"
                        fill={"none"}
                      >
                        <path
                          d="M6.57757 15.4816C5.1628 16.324 1.45336 18.0441 3.71266 20.1966C4.81631 21.248 6.04549 22 7.59087 22H16.4091C17.9545 22 19.1837 21.248 20.2873 20.1966C22.5466 18.0441 18.8372 16.324 17.4224 15.4816C14.1048 13.5061 9.89519 13.5061 6.57757 15.4816Z"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M16.5 6.5C16.5 8.98528 14.4853 11 12 11C9.51472 11 7.5 8.98528 7.5 6.5C7.5 4.01472 9.51472 2 12 2C14.4853 2 16.5 4.01472 16.5 6.5Z"
                          stroke="currentColor"
                          strokeWidth="1.5"
                        />
                      </svg>
                      <span className="text-sm mt-[2px]">
                        {listing?.personCapacity + " " + "Guest"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width={20}
                        height={20}
                        className="text-inherit text-[#c3a963]"
                        fill={"none"}
                      >
                        <path
                          d="M22 17.5H2"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M22 21V16C22 14.1144 22 13.1716 21.4142 12.5858C20.8284 12 19.8856 12 18 12H6C4.11438 12 3.17157 12 2.58579 12.5858C2 13.1716 2 14.1144 2 16V21"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M11 12V10.2134C11 9.83272 10.9428 9.70541 10.6497 9.55538C10.0395 9.24292 9.29865 9 8.5 9C7.70135 9 6.96055 9.24292 6.35025 9.55538C6.05721 9.70541 6 9.83272 6 10.2134L6 12"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M18 12V10.2134C18 9.83272 17.9428 9.70541 17.6497 9.55538C17.0395 9.24292 16.2987 9 15.5 9C14.7013 9 13.9605 9.24292 13.3503 9.55538C13.0572 9.70541 13 9.83272 13 10.2134L13 12"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M21 12V7.36057C21 6.66893 21 6.32311 20.8079 5.99653C20.6157 5.66995 20.342 5.50091 19.7944 5.16283C17.5869 3.79978 14.8993 3 12 3C9.10067 3 6.41314 3.79978 4.20558 5.16283C3.65804 5.50091 3.38427 5.66995 3.19213 5.99653C3 6.32311 3 6.66893 3 7.36057V12"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                      <span className="text-sm mt-[2px]">
                        {listing?.bedroomsNumber + " " + "Bedrooms"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width={20}
                        height={20}
                        className="text-inherit text-[#c3a963]"
                        fill={"none"}
                      >
                        <path
                          d="M22 17.5H2"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M22 21V16C22 14.1144 22 13.1716 21.4142 12.5858C20.8284 12 19.8856 12 18 12H6C4.11438 12 3.17157 12 2.58579 12.5858C2 13.1716 2 14.1144 2 16V21"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M11 12V10.2134C11 9.83272 10.9428 9.70541 10.6497 9.55538C10.0395 9.24292 9.29865 9 8.5 9C7.70135 9 6.96055 9.24292 6.35025 9.55538C6.05721 9.70541 6 9.83272 6 10.2134L6 12"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M18 12V10.2134C18 9.83272 17.9428 9.70541 17.6497 9.55538C17.0395 9.24292 16.2987 9 15.5 9C14.7013 9 13.9605 9.24292 13.3503 9.55538C13.0572 9.70541 13 9.83272 13 10.2134L13 12"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M21 12V7.36057C21 6.66893 21 6.32311 20.8079 5.99653C20.6157 5.66995 20.342 5.50091 19.7944 5.16283C17.5869 3.79978 14.8993 3 12 3C9.10067 3 6.41314 3.79978 4.20558 5.16283C3.65804 5.50091 3.38427 5.66995 3.19213 5.99653C3 6.32311 3 6.66893 3 7.36057V12"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                      <span className="text-sm mt-[2px]">
                        {listing?.bedroomsNumber + " " + "Beds"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Booking from for mobile  Start*/}
                <div className="w-full lg:hidden block lg:mt-20 md:mt-16 mt-5">
                  <div className="p-3 bg-[#f9f9f9] rounded-lg">
                    <h2 className="text-[20px] text-[#74716d] font-thin leading-none mb-6">
                      Book Now
                    </h2>
                    <form
                      onSubmit={handleBooking}
                      className=" lg:flex w-full flex-col md:flex items-center gap-4"
                    >
                      <div className="grid w-full grid-cols-1 justify-center gap-4">
                        <div className="md:flex md:items-start gap-4">
                          <CustomDateRangePicker
                            listingId={listing?.id}
                            passedStartDate={passedStartDate}
                            passedEndDate={passedEndDate}
                          />
                          <div className="relative w-full rounded-md h-[52px] lg:mt-4 md:mt-0 mt-4">
                            <input
                              type="text"
                              name=""
                              placeholder="Select Guests"
                              id="guest-field"
                              readOnly
                              value={
                                countGuest > 0 || countChild > 0
                                  ? `${
                                      countGuest > 0
                                        ? `${countGuest} Guest`
                                        : ""
                                    }${countChild > 0 ? ", " : ""}${
                                      countChild > 0
                                        ? `${countChild} Child`
                                        : ""
                                    }`
                                  : ""
                              }
                              onClick={() => setVisible(!visible)}
                              className="block text-[16px] placeholder:text-[#74716d] text-[#74716d] w-full h-[52px] lg:max-w-sm px-3 py-2 border border-gray-300 focus:outline-none focus:ring-[#aca9a5] focus:border-[#aca9a5] rounded-md"
                            />
                            {visible && (
                              <div
                                id="guest-counter"
                                ref={guestRef}
                                className="bg-white p-4 shadow-md rounded-md absolute top-14 w-full z-20"
                              >
                                <div className="flex flex-col gap-4">
                                  <div className="flex items-center justify-between gap-2">
                                    <div>
                                      <h2 className="text-[16px] leading-none pointer-events-none font-medium text-[#74716d]">
                                        Adult
                                      </h2>
                                      <span className="text-xs text-[#74716d] pointer-events-none">
                                        Ages 13 or above
                                      </span>
                                    </div>
                                    <div className="flex items-center justify-center gap-2">
                                      <div
                                        onClick={() =>
                                          handleCounter("minus", "guest")
                                        }
                                        className="border border-slate-400 w-[30px] h-[30px] rounded-full p-1 flex items-center justify-center cursor-pointer"
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          viewBox="0 0 24 24"
                                          width={20}
                                          height={20}
                                          className="text-[#74716d]"
                                          fill={"none"}
                                        >
                                          <path
                                            d="M20 12L4 12"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                      </div>
                                      <span className="text-[#74716d]">
                                        {countGuest}
                                      </span>
                                      <div
                                        onClick={() =>
                                          handleCounter("plus", "guest")
                                        }
                                        className="border border-slate-400 w-[30px] h-[30px] rounded-full p-1 flex items-center justify-center cursor-pointer"
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          viewBox="0 0 24 24"
                                          width={20}
                                          height={20}
                                          className="text-[#74716d]"
                                          fill={"none"}
                                        >
                                          <path
                                            d="M12 4V20"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                          <path
                                            d="M4 12H20"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center justify-between gap-2">
                                    <div>
                                      <h2 className="text-[16px] pointer-events-none leading-none font-medium text-[#74716d]">
                                        Children
                                      </h2>
                                      <span className="text-xs text-[#74716d] pointer-events-none">
                                        Ages 2 to 12
                                      </span>
                                    </div>
                                    <div className="flex items-center justify-center gap-2">
                                      <div
                                        onClick={() =>
                                          handleCounter("minus", "child")
                                        }
                                        className="border border-slate-400 w-[30px] h-[30px] rounded-full p-1 flex items-center justify-center cursor-pointer"
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          viewBox="0 0 24 24"
                                          width={20}
                                          height={20}
                                          className="text-[#74716d]"
                                          fill={"none"}
                                        >
                                          <path
                                            d="M20 12L4 12"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                      </div>
                                      <span className="text-[#74716d]">
                                        {countChild}
                                      </span>
                                      <div
                                        onClick={() =>
                                          handleCounter("plus", "child")
                                        }
                                        className="border border-slate-400 w-[30px] h-[30px] rounded-full p-1 flex items-center justify-center cursor-pointer"
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          viewBox="0 0 24 24"
                                          width={20}
                                          height={20}
                                          className="text-[#74716d]"
                                          fill={"none"}
                                        >
                                          <path
                                            d="M12 4V20"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                          <path
                                            d="M4 12H20"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                      </div>
                                    </div>
                                  </div>

                                  <span
                                    onClick={() => setVisible(!visible)}
                                    className="text-red-500 text-[16px] cursor-pointer mt-4 flex items-center gap-1 hover:text-red-500"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 24 24"
                                      width={20}
                                      height={20}
                                      className="text-inherit"
                                      fill={"none"}
                                    >
                                      <path
                                        d="M19.0005 4.99988L5.00049 18.9999M5.00049 4.99988L19.0005 18.9999"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                    Close
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="w-full">
                          <div className="collapse border rounded-lg">
                            <input type="checkbox" className="peer" />
                            <div className="text-[16px] flex items-center justify-between font-medium leading-none text-[#74716d] collapse-title pr-4">
                              <span className="text-[16px]">
                                Add-ons services
                              </span>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                width={20}
                                height={20}
                                className="text-inherit"
                                fill={"none"}
                              >
                                <path
                                  d="M12 4V20M20 12H4"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </div>
                            <div className="collapse-content grid grid-cols-1 gap-2">
                              {services
                                ?.filter(
                                  (service) => service?.category === "Add-On"
                                )
                                ?.map((service) => (
                                  <label
                                    key={service?._id}
                                    className="flex items-center gap-2 cursor-pointer"
                                  >
                                    <input
                                      type="checkbox"
                                      value={service?.value}
                                      checked={selectedServices.some(
                                        (item) => item?._id === service?._id
                                      )}
                                      onChange={(e) =>
                                        handleServiceChange(e, service)
                                      }
                                      className="w-4 h-4 text-[#74716d] focus:ring-[#aca9a5] border-gray-300 rounded"
                                    />
                                    <span className="text-[#74716d] text-[16px]">
                                      {service?.label}
                                    </span>
                                  </label>
                                ))}
                            </div>
                          </div>

                          {/* Display Selected Services */}
                          {selectedServices.length > 0 && (
                            <div className="mt-2">
                              <h3 className="text-[16px] font-medium text-[#74716d] mb-2">
                                Selected Adds-on Services:
                              </h3>
                              <ul className="list-disc list-inside text-[#74716d]">
                                {selectedServices.map((service) => (
                                  <li
                                    key={service._id}
                                    className="text-[16px] font-thin"
                                  >
                                    {service.label}: AED {service.value} (Per
                                    day)
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>

                        {/* <div className="w-full">
                        <div className="collapse border rounded-lg">
                          <input type="checkbox" className="peer" />
                          <div className="text-[16px] flex items-center justify-between font-medium leading-none text-[#74716d] collapse-title pr-4">
                            <span className="text-[16px]">
                              Additional Services{" "}
                            </span>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              width={20}
                              height={20}
                              className="text-inherit"
                              fill={"none"}
                            >
                              <path
                                d="M12 4V20M20 12H4"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                          <div className="collapse-content grid grid-cols-1 gap-2">
                            {services
                              ?.filter(
                                (service) => service?.category === "Additional"
                              )
                              ?.map((service) => (
                                <label
                                  key={service?._id}
                                  className="flex items-center gap-2 cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    value={service?.value}
                                    checked={selectedServices2.some(
                                      (item) => item?._id === service?._id
                                    )}
                                    onChange={(e) =>
                                      handleServiceChange2(e, service)
                                    }
                                    className="w-4 h-4 text-[#74716d] focus:ring-[#aca9a5] border-gray-300 rounded"
                                  />
                                  <span className="text-[#74716d] text-[16px]">
                                    {service?.label}
                                  </span>
                                </label>
                              ))}
                          </div>
                        </div>

                        {/* Display Selected Services */}
                        {/* {selectedServices2.length > 0 && (
                          <div className="mt-2">
                            <h3 className="text-[16px] font-medium text-[#74716d] mb-2">
                              Selected Additional Services:
                            </h3>
                            <ul className="list-disc list-inside text-[#74716d]">
                              {selectedServices2.map((service) => (
                                <li
                                  key={service._id}
                                  className="text-[16px] font-thin"
                                >
                                  {service.label}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div> */}

                        {/* Submit Button */}
                        {!subtotalData && (
                          <button
                            disabled={checkAvailabilityLoading}
                            className="mb-[2px] text-[16px] px-6 rounded-md w-full h-[50px] bg-[#c3a963]
        text-white font-normal border-none hover:bg-[#b9a15d] uppercase"
                          >
                            {checkAvailabilityLoading ? (
                              <span className="loading loading-spinner loading-md"></span>
                            ) : (
                              "Proceed"
                            )}
                          </button>
                        )}
                      </div>
                    </form>

                    <div>
                      {checkAvailabilityLoading ? (
                        <div className="flex w-52 flex-col gap-4">
                          <div className="skeleton h-4 w-full"></div>
                          <div className="skeleton h-4 w-full"></div>
                          <div className="skeleton h-4 w-full"></div>
                          <div className="skeleton h-4 w-full"></div>
                        </div>
                      ) : (
                        subtotalData && (
                          <Suspense
                            fallback={
                              <div className="skeleton h-72 w-full"></div>
                            }
                          >
                            <PricingTable
                              price={subtotalData.updatedPrice}
                              cleaningFee={subtotalData.cleaningFee}
                              nights={subtotalData.nights}
                              additional={selectedServices.reduce(
                                (total, service) => total + service.value,
                                0
                              )}
                              setTabbyTotal={setTabbyTotal}
                              guests={countGuest}
                              bedLineFee={bedLineFee?.amount}
                              cityTourismTax={cityTourismTax}
                              occupancyTax={occupancyTax}
                              vatGstTax={vatGstTax}
                              weeklyDiscount={weeklyDiscount}
                              onError={handlePricingError}
                            />
                          </Suspense>
                        )
                      )}
                      {subtotalData && (
                        <button
                          onClick={handleCheckout}
                          className="mb-[2px] text-[16px] px-6 rounded-md w-full h-[50px] bg-[#c3a963]
        text-white font-normal border-none hover:bg-[#c3a963] uppercase"
                        >
                          Reserve
                        </button>
                      )}
                      {/* {isMobile && (
                      <div>
                        {totalPrice && <TabbyPromoSnippet price={totalPrice} />}
                      </div>
                    )} */}
                    </div>
                  </div>

                  {/* <div className="p-3 bg-[#f9f9f9] rounded-lg mt-4">
                  <h2 className="text-[20px] text-[#74716d] font-thin leading-none mb-6">
                    Price Details
                  </h2>

                  <p className="font-semibold text-[16px] text-[#74716d] mt-0 mb-2">
                    Extra Price per guest:{" "}
                    <span className="font-thin text-[16px]">
                      AED {listing?.priceForExtraPerson}
                    </span>
                  </p>
                  <p className="font-semibold text-[16px] text-[#74716d] mt-0 mb-2">
                    Cleaning Fee:{" "}
                    <span className="font-thin text-[16px]">
                      AED {listing?.cleaningFee}
                    </span>
                  </p>
                  <p className="font-semibold text-[16px] text-[#74716d] mt-0">
                    Minimum no of nights:{" "}
                    <span className="font-thin text-[16px]}">
                      {listing?.minNights}
                    </span>
                  </p>
                </div> */}
                </div>
                {/* Booking from for mobile  End*/}

                <div className="p-3 bg-[#f9f9f9] rounded-lg mt-4">
                  <h2 className="text-[20px] text-[#74716d] font-thin">
                    Description
                  </h2>

                  {showMore ? (
                    sections?.map((section, index) => (
                      <p
                        key={index}
                        className="text-[#74716d] text-[16px] leading-[20px] font-thin mb-4"
                      >
                        {section}
                      </p>
                    ))
                  ) : (
                    <p className="text-[#74716d] text-[16px] leading-[20px] font-thin mb-4">
                      {`${sections[0]} ${sections[1]}`}
                    </p>
                  )}

                  <span
                    onClick={() => setShowMore(!showMore)}
                    className="block text-[16px] cursor-pointer mt-4 text-[#c3a963]"
                  >
                    {showMore ? "Show less" : "Show more"}
                  </span>
                </div>

                <div className="p-3 bg-[#f9f9f9] rounded-lg mt-4">
                  <h2 className="text-[20px] text-[#74716d] font-thin">
                    Location
                  </h2>

                  <div className="">
                    <div className="grid lg:grid-cols-2 gap-4 items-start">
                      <p className="font-semibold text-[16px] text-[#74716d] mt-0">
                        City:{" "}
                        <span className="font-thin text-[16px]">
                          {listing?.city}
                        </span>
                      </p>
                      <p className="font-semibold text-[16px] text-[#74716d] mt-0">
                        Country:{" "}
                        <span className="font-thin text-[16px]">
                          {listing?.country}
                        </span>
                      </p>
                    </div>
                    <div className="mt-5 rounded-xl overflow-hidden">
                      <Suspense
                        fallback={<div className="skeleton h-72 w-full"></div>}
                      >
                        <MapView lat={lat} lng={long} />
                      </Suspense>
                    </div>
                  </div>
                </div>

                <div className="p-3 bg-[#f9f9f9] rounded-lg mt-6">
                  <h2 className="text-[20px] text-[#74716d] font-thin">
                    Listing Details
                  </h2>
                  <div className="grid lg:grid-cols-2 gap-2 items-start">
                    <p className="font-semibold text-[16px] text-[#74716d] mt-0">
                      Property ID:{" "}
                      <span className="font-thin text-[16px]">
                        {listing?.id}
                      </span>
                    </p>

                    <p className="font-semibold text-[16px] text-[#74716d] mt-0">
                      Bathrooms:{" "}
                      <span className="font-thin text-[16px]">
                        {listing?.bathroomsNumber}
                      </span>
                    </p>

                    <p className="font-semibold text-[16px] text-[#74716d] mt-0">
                      Bedrooms:{" "}
                      <span className="font-thin text-[16px]">
                        {listing?.bedroomsNumber}
                      </span>
                    </p>

                    <p className="font-semibold text-[16px] text-[#74716d] mt-0">
                      Check In:{" "}
                      <span className="font-thin text-[16px]">
                        {formatTime(listing?.checkInTimeStart)}
                      </span>
                    </p>

                    <p className="font-semibold text-[16px] text-[#74716d] mt-0">
                      Check Out:{" "}
                      <span className="font-thin text-[16px]">
                        {formatTime(listing?.checkOutTime)}
                      </span>
                    </p>
                  </div>
                </div>

                <div className="p-3 bg-[#f9f9f9] rounded-lg mt-4">
                  <h2 className="text-[20px] text-[#74716d] font-thin">
                    Amenities
                  </h2>
                  <div>
                    <AmenitiesList amenities={listing?.listingAmenities} />
                  </div>
                </div>
              </div>
            )}

            {/* Right side content for Large device  */}
            {loading ? (
              <div className="w-full lg:flex md:flex hidden flex-col gap-4 ">
                <div className="skeleton h-6 w-28"></div>
                <div className="skeleton h-14 w-full"></div>
                <div className="skeleton h-14 w-full"></div>
                <div className="skeleton h-14 w-full"></div>
                <div className="skeleton h-14 w-full"></div>
                <div className="skeleton h-14 w-full"></div>
              </div>
            ) : (
              <div className={`lg:w-[30%] lg:block hidden min-h-full relative`}>
                <div className="p-3 rounded-lg sticky top-20 h-[calc(100vh-10rem)] overflow-y-auto">
                  <h3 className="text-[20px] text-[#74716d] font-thin leading-none mb-6">
                    Book Now
                  </h3>
                  <form
                    onSubmit={handleBooking}
                    className=" lg:flex flex-col md:flex items-center gap-4"
                  >
                    <div className="grid grid-cols-1 justify-center gap-4 w-full">
                      <CustomDateRangePicker
                        listingId={listing.id}
                        passedStartDate={passedStartDate}
                        passedEndDate={passedEndDate}
                      />
                      <div className="relative w-full rounded-md h-[52px]">
                        <input
                          type="text"
                          name=""
                          placeholder="Select Guests"
                          id="guest-field"
                          readOnly
                          value={
                            countGuest > 0 || countChild > 0
                              ? `${
                                  countGuest > 0 ? `${countGuest} Guest` : ""
                                }${countChild > 0 ? ", " : ""}`
                              : ""
                          }
                          onClick={() => setVisible(!visible)}
                          className="block text-[16px] placeholder:text-[#74716d] text-[#74716d] w-full h-[52px] max-w-sm px-3 py-2 border border-gray-300 focus:outline-none focus:ring-[#aca9a5] focus:border-[#aca9a5] rounded-md"
                        />
                        {visible && (
                          <div
                            id="guest-counter"
                            ref={guestRefTwo}
                            className="bg-white p-4 shadow-md absolute rounded-md top-14 w-full z-20"
                          >
                            <div className="flex flex-col gap-4">
                              <div className="flex items-center justify-between gap-2">
                                <div>
                                  <h2 className="text-[16px] leading-none select-none font-medium text-[#74716d]">
                                    Adult
                                  </h2>
                                  <span className="text-xs text-[#74716d] select-none">
                                    Ages 13 or above
                                  </span>
                                </div>
                                <div className="flex items-center justify-center gap-2">
                                  <div
                                    onClick={() =>
                                      handleCounter("minus", "guest")
                                    }
                                    className="border border-slate-400 w-[30px] h-[30px] rounded-full p-1 flex items-center justify-center cursor-pointer"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 24 24"
                                      width={20}
                                      height={20}
                                      className="text-[#74716d]"
                                      fill={"none"}
                                    >
                                      <path
                                        d="M20 12L4 12"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </div>
                                  <span className="text-[#74716d] select-none">
                                    {countGuest}
                                  </span>
                                  <div
                                    onClick={() =>
                                      handleCounter("plus", "guest")
                                    }
                                    className="border border-slate-400 w-[30px] h-[30px] rounded-full p-1 flex items-center justify-center cursor-pointer"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 24 24"
                                      width={20}
                                      height={20}
                                      className="text-[#74716d]"
                                      fill={"none"}
                                    >
                                      <path
                                        d="M12 4V20"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                      <path
                                        d="M4 12H20"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center justify-between gap-2">
                                <div>
                                  <h2 className="text-[16px] leading-none select-none font-medium text-[#74716d]">
                                    Children
                                  </h2>
                                  <span className="text-xs text-[#74716d] select-none">
                                    Ages 2 to 12
                                  </span>
                                </div>
                                <div className="flex items-center justify-center gap-2">
                                  <div
                                    onClick={() =>
                                      handleCounter("minus", "child")
                                    }
                                    className="border border-slate-400 w-[30px] h-[30px] rounded-full p-1 flex items-center justify-center cursor-pointer"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 24 24"
                                      width={20}
                                      height={20}
                                      className="text-[#74716d]"
                                      fill={"none"}
                                    >
                                      <path
                                        d="M20 12L4 12"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </div>
                                  <span className="text-[#74716d] select-none">
                                    {countChild}
                                  </span>
                                  <div
                                    onClick={() =>
                                      handleCounter("plus", "child")
                                    }
                                    className="border border-slate-400 w-[30px] h-[30px] rounded-full p-1 flex items-center justify-center cursor-pointer"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 24 24"
                                      width={20}
                                      height={20}
                                      className="text-[#74716d]"
                                      fill={"none"}
                                    >
                                      <path
                                        d="M12 4V20"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                      <path
                                        d="M4 12H20"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </div>
                                </div>
                              </div>

                              <span
                                onClick={() => setVisible(!visible)}
                                className="text-red-500 text-[16px] cursor-pointer mt-4 flex items-center gap-1 hover:text-red-500"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  width={20}
                                  height={20}
                                  className="text-inherit"
                                  fill={"none"}
                                >
                                  <path
                                    d="M19.0005 4.99988L5.00049 18.9999M5.00049 4.99988L19.0005 18.9999"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                                <span className="select-none">Close</span>
                              </span>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="w-full">
                        <div className="collapse border rounded-lg">
                          <input type="checkbox" />
                          <div className="text-[16px] flex items-center justify-between font-medium leading-none text-[#74716d] collapse-title pr-4">
                            <span className="text-[16px]">
                              Add-ons services
                            </span>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              width={20}
                              height={20}
                              className="text-inherit"
                              fill={"none"}
                            >
                              <path
                                d="M12 4V20M20 12H4"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                          <div className="collapse-content grid grid-cols-1 gap-2">
                            {services
                              ?.filter(
                                (service) => service?.category === "Add-On"
                              )
                              ?.map((service) => (
                                <label
                                  key={service?._id}
                                  className="flex items-center gap-2 cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    value={service?.value}
                                    checked={selectedServices.some(
                                      (item) => item?._id === service?._id
                                    )}
                                    onChange={(e) =>
                                      handleServiceChange(e, service)
                                    }
                                    className="w-4 h-4 text-[#74716d] focus:ring-[#aca9a5] border-gray-300 rounded"
                                  />
                                  <span className="text-[#74716d] text-[16px]">
                                    {service?.label}
                                  </span>
                                </label>
                              ))}
                          </div>
                        </div>

                        {/* Display Selected Services */}
                        {selectedServices.length > 0 && (
                          <div className="mt-2">
                            <h3 className="text-[16px] font-medium text-[#74716d] mb-2">
                              Selected Adds-on Services:
                            </h3>
                            <ul className="list-disc list-inside text-[#74716d]">
                              {selectedServices.map((service) => (
                                <li
                                  key={service._id}
                                  className="text-[16px] font-thin"
                                >
                                  {service.label}: AED {service.value}{" "}
                                  {service?.label === "Airport Transfer"
                                    ? "per way (Dubai International Airport)"
                                    : service?.label === "Breakfast Basket"
                                    ? "per basket"
                                    : service?.label === "E Scooter"
                                    ? "per day"
                                    : service?.label === "Early Check In"
                                    ? "per hour"
                                    : service?.label === "Late Check Out"
                                    ? "per hour"
                                    : "(per day)"}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>

                      {/* <div className="w-full">
                      <div className="collapse border rounded-lg">
                        <input type="checkbox" />
                        <div className="text-[16px] flex items-center justify-between font-medium leading-none text-[#74716d] collapse-title pr-4">
                          <span className="text-[16px]">
                            Additional Services{" "}
                          </span>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            width={20}
                            height={20}
                            className="text-inherit"
                            fill={"none"}
                          >
                            <path
                              d="M12 4V20M20 12H4"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <div className="collapse-content grid grid-cols-1 gap-2">
                          {services
                            ?.filter(
                              (service) => service?.category === "Additional"
                            )
                            ?.map((service) => (
                              <label
                                key={service?._id}
                                className="flex items-center gap-2 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  value={service?.value}
                                  checked={selectedServices2.some(
                                    (item) => item?._id === service?._id
                                  )}
                                  onChange={(e) =>
                                    handleServiceChange2(e, service)
                                  }
                                  className="w-4 h-4 text-[#74716d] focus:ring-[#aca9a5] border-gray-300 rounded"
                                />
                                <span className="text-[#74716d] text-[16px]">
                                  {service?.label}
                                </span>
                              </label>
                            ))}
                        </div>
                      </div>

                      {/* Display Selected Services */}
                      {/* {selectedServices2.length > 0 && (
                        <div className="mt-2">
                          <h3 className="text-[16px] font-medium text-[#74716d] mb-2">
                            Selected Additional Services:
                          </h3>
                          <ul className="list-disc list-inside text-[#74716d]">
                            {selectedServices2.map((service) => (
                              <li
                                key={service._id}
                                className="text-[16px] font-thin"
                              >
                                {service.label}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div> */}

                      {/* Submit Button */}
                      {!subtotalData && (
                        <button
                          disabled={checkAvailabilityLoading}
                          className="btn mb-[2px] mt-4 text-[16px] px-6 rounded-md w-full h-[50px] bg-[#c3a963]
        text-white font-normal border-none hover:bg-[#b9a15d] uppercase"
                        >
                          {checkAvailabilityLoading ? (
                            <span className="loading loading-spinner loading-md"></span>
                          ) : (
                            "Proceed"
                          )}
                        </button>
                      )}
                    </div>
                  </form>

                  <div>
                    {checkAvailabilityLoading ? (
                      <div className="flex justify-center items-center py-8">
                        <span className="loading loading-spinner loading-lg"></span>
                      </div>
                    ) : (
                      subtotalData && (
                        <Suspense
                          fallback={
                            <div className="skeleton h-72 w-full"></div>
                          }
                        >
                          <PricingTable
                            price={subtotalData.updatedPrice}
                            cleaningFee={subtotalData.cleaningFee}
                            nights={subtotalData.nights}
                            additional={selectedServices.reduce(
                              (total, service) => total + service.value,
                              0
                            )}
                            setTabbyTotal={setTabbyTotal}
                            guests={countGuest}
                            bedLineFee={bedLineFee?.amount}
                            cityTourismTax={cityTourismTax}
                            occupancyTax={occupancyTax}
                            vatGstTax={vatGstTax}
                            weeklyDiscount={weeklyDiscount}
                            onError={handlePricingError}
                          />
                        </Suspense>
                      )
                    )}
                    {subtotalData && (
                      <button
                        onClick={handleCheckout}
                        className="mb-[2px] text-[16px] px-6 rounded-md w-full h-[50px] bg-[#c3a963]
        text-white font-normal border-none hover:bg-[#b9a15d] uppercase"
                      >
                        Reserve
                      </button>
                    )}
                  </div>
                  <div className="md:block hidden">
                    {tabbyTotal && (
                      <TabbyPromoSnippet price={tabbyTotal.toFixed(2)} />
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default SingleListing;
